import { useForm } from "react-hook-form";
import {
  AlignItems,
  Box,
  ButtonVariant,
  ButtonSize,
  Checkbox,
  Field,
  Form,
  FormFieldStatus,
  Hint,
  Label,
  P,
  Space,
  TextAlign,
  ColorPreset,
  TypePreset,
  FontWeight,
  PlainLink,
  PlainButton,
  HoverEffect,
  ResponsiveValue,
  SpaceScale,
} from "@gocardless/flux-react";
import {
  FactorType,
  TemporaryAccessTokenCreateRequestBody,
} from "@gocardless/api/dashboard/types";
import { Trans } from "@lingui/macro";

import { AuthFactorMetaData } from "../types";

import { getInputErrorStatus, VALID_OTP_CODE } from "src/utils/inputValidation";
import { getRouteURL, Route } from "src/common/routing";
import { OneTimePasswordInput } from "src/components/ui/OneTimePasswordInput/OneTimePasswordInput";
import { SubmitButton } from "src/components/shared/SubmitButton";

interface Props {
  authFactor: AuthFactorMetaData;
  submit: (requestBody: TemporaryAccessTokenCreateRequestBody) => void;
  isSubmitting: boolean;
  triggerRecoveryCode: () => void;
  spaceAbove?: ResponsiveValue<SpaceScale>;
}

const getOTPLabel = ({
  factor_type,
  phone_number_ending,
}: AuthFactorMetaData) => {
  if (factor_type === FactorType.Sms) {
    return (
      <Trans id="sign-in.otp.label.sms">
        Enter the 6-digit code that we have sent to your phone ending in{" "}
        {phone_number_ending}
      </Trans>
    );
  }
  return (
    <Trans id="sign-in.otp.label.authenticator">
      Enter the 6-digit code from your authenticator app
    </Trans>
  );
};

export const OneTimePassword: React.FC<Props> = ({
  authFactor,
  submit,
  isSubmitting,
  triggerRecoveryCode,
  spaceAbove,
}) => {
  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      otp_code: "",
      trust_device: false,
      delay_password_reset: !!authFactor.delay_password_reset,
    },
    mode: "onChange",
    shouldUnregister: true,
  });

  const code = watch("otp_code");
  const isInvalidCode = errors.otp_code?.type === "pattern";
  const submitButtonDisabled = !code || isInvalidCode || isSubmitting;

  return (
    <Box spaceAbove={spaceAbove}>
      <Form
        data-reading-optimized
        onSubmit={handleSubmit((data) => submit(data))}
      >
        <Field>
          <Label htmlFor="otp_code"> {getOTPLabel(authFactor)} </Label>
          <OneTimePasswordInput
            id="otp_code"
            pattern="[0-9]*"
            {...register("otp_code", {
              required: true,
              pattern: VALID_OTP_CODE,
            })}
            status={getInputErrorStatus(!!isInvalidCode)}
          />
          {errors.otp_code && errors.otp_code.type === "pattern" && (
            <Hint status={FormFieldStatus.Danger}>
              <Trans id="settings.user.mfa.sms.dialog.enter-code.invalid-message">
                The code should be 6 digits long.
              </Trans>
            </Hint>
          )}
        </Field>
        <Space v={1.5} />
        <Checkbox
          id="trust_device"
          description={
            <Trans id="sign-in.otp.grace-period-description">
              Not recommended if using a shared device
            </Trans>
          }
          {...register("trust_device")}
        >
          <Trans id="sign-in.otp.skip-this-step">
            Skip this step for 14 days
          </Trans>
          <Space v={0.25} />
        </Checkbox>
        <Space v={1.5} />
        <Box
          width="100%"
          layout="flex"
          flexDirection={["column", "row", null, null]}
          alignItems={[null, AlignItems.Center, null, null]}
        >
          <SubmitButton
            variant={ButtonVariant.PrimaryOnLight}
            size={[ButtonSize.Md, null, null, ButtonSize.Lg]}
            disabled={submitButtonDisabled}
            data-testid="otp-submit-button"
            isSubmitting={isSubmitting}
          >
            <Trans id="Submit"> Submit </Trans>
          </SubmitButton>
          <Box gutterH={[0, 1.5, null, null]} gutterV={[0.75, 0, null, null]} />
          <PlainLink
            href={getRouteURL({
              route: Route.SignIn,
            })}
          >
            <P
              textAlign={TextAlign.Center}
              color={ColorPreset.TextOnLight_01}
              preset={TypePreset.Body_02}
              weight={FontWeight.SemiBold}
            >
              <Trans id="Go back">Go back</Trans>
            </P>
          </PlainLink>
        </Box>
        <Space v={3} />
        <PlainButton
          textDecoration="underline"
          effect={HoverEffect.TextDecoration}
          onClick={triggerRecoveryCode}
        >
          <Trans id="sign-in.otp.recovery-code">
            Sign in with your recovery code
          </Trans>
        </PlainButton>
      </Form>
    </Box>
  );
};
